import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Spin,
  Typography,
  Upload,
  message,
} from "antd";
import {
  ArrowLeftOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../api/api";

const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const boldLabel = (label) => <span style={{ fontWeight: 600 }}>{label}</span>;

const STATUS_OPTIONS = [
  { label: "Draft", value: "draft" },
  { label: "Published", value: "published" },
  { label: "Archived", value: "archived" },
];

const TOUR_OPERATOR_OPTIONS = [
  { label: "Caribe Sol", value: "CAH" },
  { label: "Enjoy Travel Group", value: "ETG" },
  { label: "Holasun", value: "HOL" },
  { label: "Sunquest Vacations", value: "SQV" },
  { label: "The Special Travel", value: "STC" },
  { label: "Sunwing Vacations", value: "SWG" },
  { label: "TravelBrands", value: "TBA" },
  { label: "Air Canada Vacations", value: "VAC" },
  { label: "Transat", value: "VAT" },
  { label: "Vacances Westjet Quebec", value: "VWQ" },
  { label: "WestJet Vacations", value: "WJS" },
];

const DESTINATION_IMAGE_UPLOAD_ENDPOINT = `${API_BASE_URL}destinationImageUpload`;
const DEAL_GATEWAY_OPTIONS_ENDPOINT = `${API_BASE_URL}dealGatewayOptions`;
const DEAL_DESTINATION_OPTIONS_ENDPOINT = `${API_BASE_URL}dealDestinationOptions`;

const getUploadFile = (file) => {
  if (file?.originFileObj instanceof File) {
    return file.originFileObj;
  }

  if (file instanceof File) {
    return file;
  }

  return null;
};

const ImageUrlPreview = ({ url, alt = "Preview" }) => {
  const normalizedUrl = (url || "").trim();

  if (!normalizedUrl) {
    return null;
  }

  return (
    <div style={{ marginTop: 8 }}>
      <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
        Preview
      </Text>
      <div
        style={{
          border: "1px solid #f0f0f0",
          borderRadius: 8,
          padding: 8,
          background: "#fafafa",
          display: "inline-block",
        }}
      >
        <img
          src={normalizedUrl}
          alt={alt}
          style={{
            width: 280,
            maxWidth: "100%",
            height: 160,
            objectFit: "cover",
            borderRadius: 6,
            display: "block",
          }}
        />
      </div>
    </div>
  );
};

const CoverImageField = ({ form, token }) => {
  const [uploading, setUploading] = useState(false);
  const currentValue = Form.useWatch("cover_image_url", form);

  const handleUploadChange = async (info) => {
    const file = getUploadFile(info.file);

    if (!file) {
      message.error("Invalid file selected");
      return;
    }

    const formData = new FormData();
    formData.append("category", "deals");
    formData.append("image", file);

    const hide = message.loading("Uploading image...", 0);
    setUploading(true);

    try {
      const res = await fetch(DESTINATION_IMAGE_UPLOAD_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });
      const rawResponse = await res.text();
      let result = null;

      try {
        result = rawResponse ? JSON.parse(rawResponse) : null;
      } catch (error) {
        throw new Error("Upload endpoint returned an invalid response");
      }

      if (!res.ok || !result || !result.success || !result.url) {
        throw new Error(result?.error || result?.message || "Upload failed");
      }

      form.setFieldValue("cover_image_url", result.url);
      message.success("Image uploaded");
    } catch (error) {
      console.error(error);
      message.error(error.message || "Upload failed");
    } finally {
      hide();
      setUploading(false);
    }
  };

  return (
    <>
      <Form.Item label={boldLabel("Cover Image")}>
        <Form.Item name="cover_image_url" noStyle>
          <Input type="hidden" />
        </Form.Item>
        <Upload
          beforeUpload={() => false}
          showUploadList={false}
          accept="image/*"
          maxCount={1}
          onChange={handleUploadChange}
        >
          <Button icon={<UploadOutlined />} loading={uploading}>
            Upload Image
          </Button>
        </Upload>
      </Form.Item>
      <ImageUrlPreview url={currentValue} alt="Deal cover" />
    </>
  );
};

const normalizeTags = (value) =>
  (Array.isArray(value) ? value : [])
    .map((item) => String(item || "").trim())
    .filter(Boolean);

const buildGatewayQuery = (gatewayCodes) =>
  gatewayCodes
    .map((code) => String(code || "").trim().toUpperCase())
    .filter(Boolean)
    .join(",");

const DealDestinationSelector = ({
  countries,
  loading,
  value,
  onChange,
}) => {
  const selectedCodeSet = useMemo(
    () => new Set(normalizeTags(value)),
    [value]
  );

  const updateSelectedCodes = (nextCodes) => {
    onChange(Array.from(new Set(nextCodes)));
  };

  const handleCountryChange = (country, checked) => {
    const cityCodes = country.cities.map((city) => String(city.destination_code));
    const nextSet = new Set(selectedCodeSet);

    cityCodes.forEach((cityCode) => {
      if (checked) {
        nextSet.add(cityCode);
      } else {
        nextSet.delete(cityCode);
      }
    });

    updateSelectedCodes(Array.from(nextSet));
  };

  const handleCityChange = (cityCode, checked) => {
    const nextSet = new Set(selectedCodeSet);
    const normalizedCode = String(cityCode);

    if (checked) {
      nextSet.add(normalizedCode);
    } else {
      nextSet.delete(normalizedCode);
    }

    updateSelectedCodes(Array.from(nextSet));
  };

  if (loading) {
    return (
      <div style={{ padding: "24px 0", textAlign: "center" }}>
        <Spin size="small" />
      </div>
    );
  }

  if (!countries.length) {
    return <Text type="secondary">Select one or more departure gateways first.</Text>;
  }

  const totalCityCount = countries.reduce(
    (sum, country) => sum + country.cities.length,
    0
  );

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          padding: "10px 14px",
          borderRadius: 12,
          background:
            "linear-gradient(135deg, rgba(24,144,255,0.08), rgba(82,196,26,0.08))",
          border: "1px solid rgba(24,144,255,0.14)",
        }}
      >
        <Text strong>{countries.length} countries available</Text>
        <Text type="secondary">{totalCityCount} cities available</Text>
        <Text type="secondary">{selectedCodeSet.size} cities selected</Text>
      </div>

      <Row gutter={[16, 16]}>
        {countries.map((country) => {
          const cityCodes = country.cities.map((city) => String(city.destination_code));
          const hasCities = cityCodes.length > 0;
          const selectedCount = cityCodes.filter((cityCode) =>
            selectedCodeSet.has(cityCode)
          ).length;
          const allChecked = hasCities && selectedCount === cityCodes.length;
          const indeterminate =
            selectedCount > 0 && selectedCount < cityCodes.length;

          return (
            <Col xs={24} md={12} xl={8} key={`${country.country_name}-${country.country_code || "group"}`}>
              <div
                style={{
                  height: "100%",
                  border: allChecked
                    ? "1px solid rgba(24, 144, 255, 0.32)"
                    : "1px solid #e8edf3",
                  borderRadius: 16,
                  overflow: "hidden",
                  background: "#fff",
                  boxShadow: allChecked
                    ? "0 8px 24px rgba(24, 144, 255, 0.08)"
                    : "0 4px 14px rgba(15, 23, 42, 0.04)",
                }}
              >
                <div
                  style={{
                    padding: "14px 16px",
                    background: allChecked
                      ? "linear-gradient(135deg, rgba(24,144,255,0.12), rgba(24,144,255,0.04))"
                      : "#fafbfd",
                    borderBottom: "1px solid #eef2f6",
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 12,
                  }}
                >
                  <Checkbox
                    checked={allChecked}
                    indeterminate={indeterminate}
                    disabled={!hasCities}
                    onChange={(event) =>
                      handleCountryChange(country, event.target.checked)
                    }
                  >
                    <Text strong style={{ fontSize: 16 }}>
                      {country.country_name}
                    </Text>
                  </Checkbox>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      justifyContent: "flex-end",
                    }}
                  >
                    <span
                      style={{
                        padding: "3px 10px",
                        borderRadius: 999,
                        background: "#f0f5ff",
                        color: hasCities ? "#1d39c4" : "#8c8c8c",
                        fontSize: 12,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {country.cities.length} {country.cities.length === 1 ? "city" : "cities"}
                    </span>
                    <span
                      style={{
                        padding: "3px 10px",
                        borderRadius: 999,
                        background: selectedCount > 0 ? "#f6ffed" : "#fafafa",
                        color: selectedCount > 0 ? "#389e0d" : "#8c8c8c",
                        fontSize: 12,
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {selectedCount} selected
                    </span>
                  </div>
                </div>

                {hasCities ? (
                  <Checkbox.Group
                    value={cityCodes.filter((cityCode) => selectedCodeSet.has(cityCode))}
                    style={{ width: "100%" }}
                  >
                    <div style={{ padding: 16 }}>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 10,
                        }}
                      >
                        {country.cities.map((city) => (
                          <div
                            key={`${country.country_name}-${city.destination_code}`}
                            style={{
                              flex: "0 1 auto",
                              maxWidth: "100%",
                            }}
                          >
                            <div
                              style={{
                                border: selectedCodeSet.has(String(city.destination_code))
                                  ? "1px solid rgba(24, 144, 255, 0.28)"
                                  : "1px solid #eef2f6",
                                background: selectedCodeSet.has(String(city.destination_code))
                                  ? "rgba(24, 144, 255, 0.06)"
                                  : "#fff",
                                borderRadius: 12,
                                padding: "8px 12px",
                                minHeight: 40,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <Checkbox
                                value={city.destination_code}
                                onChange={(event) =>
                                  handleCityChange(
                                    city.destination_code,
                                    event.target.checked
                                  )
                                }
                              >
                                {city.destination_name}
                              </Checkbox>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Checkbox.Group>
                ) : (
                  <div
                    style={{
                      padding: "12px 16px 14px",
                      color: "#8c8c8c",
                      fontSize: 13,
                    }}
                  >
                    No city breakdown available for this destination group.
                  </div>
                )}
              </div>
            </Col>
          );
        })}
      </Row>
    </Space>
  );
};

const toFormValues = (deal) => ({
  tour_operator_code: deal.tour_operator_code || undefined,
  booking_window:
    deal.booking_start_date && deal.booking_end_date
      ? [dayjs(deal.booking_start_date), dayjs(deal.booking_end_date)]
      : [],
  departure_window:
    deal.departure_start_date && deal.departure_end_date
      ? [dayjs(deal.departure_start_date), dayjs(deal.departure_end_date)]
      : [],
  deal_header_en: deal.deal_header_en || "",
  deal_header_zh_cn: deal.deal_header_zh_cn || "",
  deal_header_zh_tw: deal.deal_header_zh_tw || "",
  deal_detail_en: deal.deal_detail_en || "",
  deal_detail_zh_cn: deal.deal_detail_zh_cn || "",
  deal_detail_zh_tw: deal.deal_detail_zh_tw || "",
  terms_and_conditions_en: deal.terms_and_conditions_en || "",
  terms_and_conditions_zh_cn: deal.terms_and_conditions_zh_cn || "",
  terms_and_conditions_zh_tw: deal.terms_and_conditions_zh_tw || "",
  deal_url: deal.deal_url || "",
  cover_image_url: deal.cover_image_url || "",
  status: deal.status || "draft",
  destination_codes: normalizeTags(deal.destination_codes),
  departure_iata_codes: normalizeTags(deal.departure_iata_codes),
  duration_days: normalizeTags(deal.duration_days),
});

const DealEditorPage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { dealId } = useParams();
  const token = localStorage.getItem("token");
  const isEditing = Boolean(dealId);
  const pendingInitialDestinationCodesRef = useRef(null);

  const [saving, setSaving] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [gatewayOptions, setGatewayOptions] = useState([]);
  const [destinationCountries, setDestinationCountries] = useState([]);
  const [destinationLoading, setDestinationLoading] = useState(false);
  const selectedGatewayCodes = Form.useWatch("departure_iata_codes", form) || [];

  useEffect(() => {
    const loadData = async () => {
      setPageLoading(true);

      try {
        const gatewayRequest = fetch(DEAL_GATEWAY_OPTIONS_ENDPOINT, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }).then((res) => res.json().then((result) => ({ res, result })));

        const dealRequest = isEditing
          ? fetch(`${API_BASE_URL}deals&id=${dealId}`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }).then((res) => res.json().then((result) => ({ res, result })))
          : Promise.resolve(null);

        const [gatewayResponse, dealResponse] = await Promise.all([
          gatewayRequest,
          dealRequest,
        ]);

        if (!gatewayResponse.res.ok || !gatewayResponse.result.success) {
          message.error(
            gatewayResponse.result.error || "Failed to load gateway options"
          );
        } else {
          const gateways = Array.isArray(gatewayResponse.result.data)
            ? gatewayResponse.result.data
            : [];

          setGatewayOptions(
            gateways.map((item) => ({
              label: `${item.gateway_name} (${item.gateway_code})`,
              value: String(item.gateway_code || "").toUpperCase(),
            }))
          );
        }

        if (isEditing) {
          if (!dealResponse.res.ok || !dealResponse.result.success) {
            message.error(dealResponse.result.error || "Failed to load deal");
            return;
          }

          const formValues = toFormValues(dealResponse.result.data || {});
          pendingInitialDestinationCodesRef.current = formValues.destination_codes;
          form.setFieldsValue(formValues);
        } else {
          pendingInitialDestinationCodesRef.current = [];
          form.setFieldsValue({
            status: "draft",
            destination_codes: [],
            departure_iata_codes: [],
            duration_days: [],
          });
        }
      } catch (error) {
        console.error(error);
        message.error("Failed to load deal data");
      } finally {
        setPageLoading(false);
      }
    };

    loadData();
  }, [dealId, form, isEditing, token]);

  useEffect(() => {
    const gatewayQuery = buildGatewayQuery(selectedGatewayCodes);

    if (!gatewayQuery) {
      setDestinationCountries([]);
      setDestinationLoading(false);
      form.setFieldValue("destination_codes", []);
      return;
    }

    let cancelled = false;

    const loadDestinationOptions = async () => {
      setDestinationLoading(true);

      try {
        const res = await fetch(
          `${DEAL_DESTINATION_OPTIONS_ENDPOINT}&gateway_codes=${encodeURIComponent(
            gatewayQuery
          )}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const result = await res.json();

        if (cancelled) {
          return;
        }

        if (!res.ok || !result.success) {
          message.error(result.error || "Failed to load destination options");
          setDestinationCountries([]);
          return;
        }

        const countries = Array.isArray(result.data?.countries)
          ? result.data.countries
          : [];
        const allowedCityCodes = new Set(
          countries.flatMap((country) =>
            Array.isArray(country.cities)
              ? country.cities.map((city) => String(city.destination_code))
              : []
          )
        );
        const sourceDestinationCodes =
          pendingInitialDestinationCodesRef.current !== null
            ? normalizeTags(pendingInitialDestinationCodesRef.current)
            : normalizeTags(form.getFieldValue("destination_codes"));

        setDestinationCountries(countries);
        form.setFieldValue(
          "destination_codes",
          sourceDestinationCodes.filter((code) => allowedCityCodes.has(code))
        );
        pendingInitialDestinationCodesRef.current = null;
      } catch (error) {
        if (!cancelled) {
          console.error(error);
          message.error("Failed to load destination options");
          setDestinationCountries([]);
        }
      } finally {
        if (!cancelled) {
          setDestinationLoading(false);
        }
      }
    };

    loadDestinationOptions();

    return () => {
      cancelled = true;
    };
  }, [form, selectedGatewayCodes, token]);

  const durationOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => {
        const value = String(index + 5);
        return { label: `${value} days`, value };
      }),
    []
  );

  const buildPayload = (values) => ({
    id: isEditing ? Number(dealId) : undefined,
    tour_operator_code: values.tour_operator_code,
    booking_start_date: values.booking_window?.[0]?.format("YYYY-MM-DD"),
    booking_end_date: values.booking_window?.[1]?.format("YYYY-MM-DD"),
    departure_start_date: values.departure_window?.[0]?.format("YYYY-MM-DD"),
    departure_end_date: values.departure_window?.[1]?.format("YYYY-MM-DD"),
    deal_header_en: values.deal_header_en?.trim() || "",
    deal_header_zh_cn: values.deal_header_zh_cn?.trim() || "",
    deal_header_zh_tw: values.deal_header_zh_tw?.trim() || "",
    deal_detail_en: values.deal_detail_en?.trim() || "",
    deal_detail_zh_cn: values.deal_detail_zh_cn?.trim() || "",
    deal_detail_zh_tw: values.deal_detail_zh_tw?.trim() || "",
    terms_and_conditions_en: values.terms_and_conditions_en?.trim() || "",
    terms_and_conditions_zh_cn:
      values.terms_and_conditions_zh_cn?.trim() || "",
    terms_and_conditions_zh_tw: values.terms_and_conditions_zh_tw?.trim() || "",
    deal_url: values.deal_url?.trim() || null,
    cover_image_url: values.cover_image_url?.trim() || null,
    status: values.status,
    destination_codes: normalizeTags(values.destination_codes),
    departure_iata_codes: normalizeTags(values.departure_iata_codes).map((code) =>
      code.toUpperCase()
    ),
    duration_days: normalizeTags(values.duration_days),
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = buildPayload(values);

      setSaving(true);
      const res = await fetch(`${API_BASE_URL}deals`, {
        method: isEditing ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const result = await res.json();

      if (res.ok && result.success) {
        message.success(isEditing ? "Deal updated" : "Deal created");
        navigate("/deals");
      } else {
        message.error(result.error || "Failed to save deal");
      }
    } catch (error) {
      if (error?.errorFields) {
        const firstErrorField = error.errorFields[0]?.name;
        if (firstErrorField) {
          form.scrollToField(firstErrorField, {
            behavior: "smooth",
            block: "center",
          });
        }
        message.error("Please complete the required fields before saving");
        return;
      }

      console.error(error);
      message.error("Failed to save deal");
    } finally {
      setSaving(false);
    }
  };

  if (pageLoading) {
    return (
      <div style={{ padding: "80px 0", textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space direction="vertical" size={4}>
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/deals")}
              style={{ paddingLeft: 0 }}
            >
              Back to Tour Operator Deals
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {isEditing ? `Edit Deal #${dealId}` : "Create Deal"}
            </Title>
            <Text type="secondary">
              Manage tour operator promotion content, destinations, departures, and durations.
            </Text>
          </Space>
        </Col>
      </Row>

      <Form form={form} layout="vertical">
        <Card title="Deal Settings" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                label={boldLabel("Tour Operator")}
                name="tour_operator_code"
                rules={[{ required: true, message: "Please select a tour operator" }]}
              >
                <Select options={TOUR_OPERATOR_OPTIONS} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                label={boldLabel("Status")}
                name="status"
                rules={[{ required: true, message: "Please select a status" }]}
              >
                <Select options={STATUS_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={boldLabel("Booking Window")}
                name="booking_window"
                rules={[{ required: true, message: "Please select the booking window" }]}
              >
                <RangePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={boldLabel("Departure Window")}
                name="departure_window"
                rules={[{ required: true, message: "Please select the departure window" }]}
              >
                <RangePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <CoverImageField form={form} token={token} />

          <Form.Item
            label={boldLabel("Tour Operator Deal URL")}
            name="deal_url"
            rules={[
              {
                type: "url",
                warningOnly: true,
                message: "Please enter a valid URL",
              },
            ]}
          >
            <Input placeholder="https://..." />
          </Form.Item>

          <Form.Item
            label={boldLabel("Departure Gateways")}
            name="departure_iata_codes"
          >
            <Checkbox.Group style={{ width: "100%" }}>
              <Row gutter={[12, 12]}>
                {gatewayOptions.map((option) => (
                  <Col xs={24} sm={12} md={12} lg={8} key={option.value}>
                    <Checkbox value={option.value}>{option.label}</Checkbox>
                  </Col>
                ))}
              </Row>
            </Checkbox.Group>
          </Form.Item>

          <Form.Item
            label={boldLabel("Destinations")}
            name="destination_codes"
          >
            <DealDestinationSelector
              countries={destinationCountries}
              loading={destinationLoading}
            />
          </Form.Item>

          <Form.Item label={boldLabel("Durations (Days)")} name="duration_days">
            <Checkbox.Group options={durationOptions} />
          </Form.Item>
        </Card>

        <Card title="English" style={{ marginBottom: 16 }}>
          <Form.Item
            label={boldLabel("Deal Header")}
            name="deal_header_en"
            rules={[{ required: true, message: "Please enter the English deal header" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label={boldLabel("Deal Detail")} name="deal_detail_en">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item
            label={boldLabel("Terms and Conditions")}
            name="terms_and_conditions_en"
          >
            <TextArea rows={3} />
          </Form.Item>
        </Card>

        <Card title="Simplified Chinese" style={{ marginBottom: 16 }}>
          <Form.Item
            label={boldLabel("Deal Header")}
            name="deal_header_zh_cn"
            rules={[{ required: true, message: "Please enter the Simplified Chinese deal header" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label={boldLabel("Deal Detail")} name="deal_detail_zh_cn">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item
            label={boldLabel("Terms and Conditions")}
            name="terms_and_conditions_zh_cn"
          >
            <TextArea rows={3} />
          </Form.Item>
        </Card>

        <Card title="Traditional Chinese" style={{ marginBottom: 16 }}>
          <Form.Item
            label={boldLabel("Deal Header")}
            name="deal_header_zh_tw"
            rules={[{ required: true, message: "Please enter the Traditional Chinese deal header" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label={boldLabel("Deal Detail")} name="deal_detail_zh_tw">
            <TextArea rows={3} />
          </Form.Item>
          <Form.Item
            label={boldLabel("Terms and Conditions")}
            name="terms_and_conditions_zh_tw"
          >
            <TextArea rows={3} />
          </Form.Item>
        </Card>
      </Form>

      <div
        style={{
          position: "fixed",
          right: 32,
          bottom: 24,
          zIndex: 1000,
          background: "rgba(255, 255, 255, 0.96)",
          border: "1px solid #f0f0f0",
          borderRadius: 12,
          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
          padding: 12,
          backdropFilter: "blur(8px)",
        }}
      >
        <Button type="primary" loading={saving} onClick={handleSubmit}>
          Save Deal
        </Button>
      </div>
    </div>
  );
};

export default DealEditorPage;
